import { i18n, I18n } from '@lingui/core';
import { LanguageEnum, logToExternalErrorHandlers } from '@luminovo/commons';
import { detectLocale, LocaleConfig } from '@luminovo/frontend-utils';
import { setTag } from '@sentry/react';

import * as enMessages from '@/locales/en/merged';

import { setLanguageCookieConsent } from './services';
import { isAppStale } from './staleApp';

const enLocale: LocaleConfig = {
    locale: 'en',
    messages: enMessages.messages,
};

// Not read off `i18n.locale`: another activation of the shared singleton would make us skip loading the catalog.
let activatedLocale: LanguageEnum | undefined;

const activateLocale = ({ locale, messages }: LocaleConfig) => {
    // Each `load` and `activate` emits `change`, which re-renders the whole tree below `useLingui`.
    if (activatedLocale === locale) {
        return;
    }
    activatedLocale = locale;
    // `@luminovo/frontend-utils` reads the locale off this same singleton, so its formatters follow.
    i18n.load(locale, messages);
    i18n.activate(locale);
    setLanguageCookieConsent({ locale, messages });
};

// The language asked for last; an older request's catalog doesn't activate. Otherwise the browser language's catalog
// that `initializeI18n` asks for at startup could land after the user's and switch the app back.
let requestedLocale: string | undefined;

const buildLocaleConfig = (locale: LanguageEnum, messages: any): LocaleConfig => {
    return {
        locale,
        messages: messages.messages,
    };
};

export const loadLocale = async (locale: string): Promise<void> => {
    requestedLocale = locale;
    let localeConfig: LocaleConfig | null = null;
    if (locale.startsWith('en')) {
        localeConfig = enLocale;
    } else if (locale.startsWith('de')) {
        const { messages } = await import('@/locales/de/merged');
        localeConfig = buildLocaleConfig('de', { messages });
    } else if (locale.startsWith('fr')) {
        const { messages } = await import('@/locales/fr/merged');
        localeConfig = buildLocaleConfig('fr', { messages });
    } else if (locale.startsWith('es')) {
        const { messages } = await import('@/locales/es/merged');
        localeConfig = buildLocaleConfig('es', { messages });
    } else if (locale.startsWith('it')) {
        const { messages } = await import('@/locales/it/merged');
        localeConfig = buildLocaleConfig('it', { messages });
    } else if (locale.startsWith('th')) {
        const { messages } = await import('@/locales/th/merged');
        localeConfig = buildLocaleConfig('th', { messages });
    } else if (locale.startsWith('zh')) {
        const { messages } = await import('@/locales/zh/merged');
        localeConfig = buildLocaleConfig('zh', { messages });
    } else if (locale.startsWith('ja')) {
        const { messages } = await import('@/locales/ja/merged');
        localeConfig = buildLocaleConfig('ja', { messages });
    } else if (locale.startsWith('hi')) {
        const { messages } = await import('@/locales/hi/merged');
        localeConfig = buildLocaleConfig('hi', { messages });
    } else if (locale.startsWith('cs')) {
        const { messages } = await import('@/locales/cs/merged');
        localeConfig = buildLocaleConfig('cs', { messages });
    } else if (locale.startsWith('nl')) {
        const { messages } = await import('@/locales/nl/merged');
        localeConfig = buildLocaleConfig('nl', { messages });
    } else if (locale.startsWith('hu')) {
        const { messages } = await import('@/locales/hu/merged');
        localeConfig = buildLocaleConfig('hu', { messages });
    } else if (locale.startsWith('pt')) {
        const { messages } = await import('@/locales/pt/merged');
        localeConfig = buildLocaleConfig('pt', { messages });
    } else if (locale.startsWith('fi')) {
        const { messages } = await import('@/locales/fi/merged');
        localeConfig = buildLocaleConfig('fi', { messages });
    } else if (locale.startsWith('sv')) {
        const { messages } = await import('@/locales/sv/merged');
        localeConfig = buildLocaleConfig('sv', { messages });
    } else if (locale.startsWith('pl')) {
        const { messages } = await import('@/locales/pl/merged');
        localeConfig = buildLocaleConfig('pl', { messages });
    } else if (locale.startsWith('tr')) {
        const { messages } = await import('@/locales/tr/merged');
        localeConfig = buildLocaleConfig('tr', { messages });
    }

    if (localeConfig && requestedLocale === locale) {
        setTag('detected_locale', localeConfig.locale);
        activateLocale(localeConfig);
    }
};

let browserLanguageLoad: Promise<void> = Promise.resolve();

/** Starts in English and switches once the detected locale has loaded. */
export const initializeI18n = (): I18n => {
    activateLocale(enLocale);
    const detected = detectLocale();

    browserLanguageLoad = loadLocale(detected).catch((error: unknown) => {
        if (!isAppStale()) {
            logToExternalErrorHandlers(error instanceof Error ? error : new Error(String(error)));
        }
    });

    return i18n;
};

/**
 * Resolves once the browser's language, which `initializeI18n` loads, has loaded or failed to. Never rejects.
 *
 * The public pages wait for it in their `loader`. Text from the `t` macro is read while a page renders, and nothing
 * renders a page again when the language changes, so a page that rendered before would stay in English. The
 * signed-in pages render in the user's language instead, which their session loads.
 */
export function browserLanguageLoaded(): Promise<void> {
    return browserLanguageLoad;
}

/**
 * Sort values by translation
 */
export type ValueWithTranslation<T> = { value: T; translation: string };

export function sortValuesByTranslation<T extends { translation: string }>(a: T, b: T) {
    return a.translation.localeCompare(b.translation);
}
